import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import { t } from "../lib/translations";
import { LogOut, Menu, X, Shield, Wallet, Home, Store, MessageCircle, MessageSquare, ListChecks, Headphones, Inbox, LayoutDashboard, ChevronDown, Sparkles, Bell, Target, HelpCircle, Bot, Accessibility, UserCircle, ShoppingCart, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}`.replace(/^http/, "ws") + "/api/ws";
const LANG_OPTIONS = [
  { code: "he", flag: "https://flagcdn.com/24x18/il.png", name: "עברית" },
  { code: "ru", flag: "https://flagcdn.com/24x18/ru.png", name: "Русский" },
  { code: "en", flag: "https://flagcdn.com/24x18/gb.png", name: "English" },
];

function LangDropdown() {
  const { lang, setLang } = useLanguage();
  const [justSelected, setJustSelected] = useState(null);
  const current = LANG_OPTIONS.find(o => o.code === lang) || LANG_OPTIONS[0];

  const handleSelect = (code) => {
    setJustSelected(code);
    setLang(code);
    setTimeout(() => setJustSelected(null), 400);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 h-8 px-1.5 rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
          aria-label="Language">
          <img src={current.flag} alt={current.name} style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2 }} />
          <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{current.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        style={{
          width: 168,
          background: "var(--bg-primary)",
          border: "1px solid var(--border-color)",
          borderRadius: 14,
          padding: "6px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        }}>
        {LANG_OPTIONS.map(({ code, flag, name }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleSelect(code)}
            className={`cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium${justSelected === code ? " animate-lang-pop" : ""}`}
            style={{
              color: lang === code ? "#8B5CF6" : "var(--text-secondary)",
              background: lang === code && justSelected !== code ? "rgba(139,92,246,0.08)" : "transparent",
            }}>
            <div className="flex items-center gap-2.5 w-full">
              <img
                src={flag}
                alt={name}
                className={justSelected === code ? "animate-lang-flag" : ""}
                style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
              />
              <span className="flex-1">{name}</span>
              {lang === code && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "#8B5CF6" }} />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar() {
  const { user, login, logout } = useAuth();
  const { lang, setLang, isRTL } = useLanguage();
  const { fetchCart, itemCount, setSidebarOpen, sidebarOpen } = useCart();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [supportWidgetOpen, setSupportWidgetOpen] = useState(false);
  const [crmUnreadCount, setCrmUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [justSelectedLang, setJustSelectedLang] = useState(null);

  const handleLangSelect = (code) => {
    setJustSelectedLang(code);
    setLang(code);
    setTimeout(() => setJustSelectedLang(null), 400);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wsRef = useRef(null);
  const wsRetryRef = useRef(null);
  const wsPingRef = useRef(null);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const open = () => setMobileOpen(true);
    const close = () => setMobileOpen(false);
    window.addEventListener('tour:open-mobile-nav', open);
    window.addEventListener('tour:close-mobile-nav', close);
    return () => { window.removeEventListener('tour:open-mobile-nav', open); window.removeEventListener('tour:close-mobile-nav', close); };
  }, []);

  useEffect(() => {
    const h = (e) => setSupportWidgetOpen(e.detail?.open ?? false);
    window.addEventListener("support-open-state", h);
    return () => window.removeEventListener("support-open-state", h);
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const [messagesRes, supportRes] = await Promise.all([
        fetch(`${API}/messages/unread-count`, { credentials: "include" }),
        fetch(`${API}/tickets/unread-count`, { credentials: "include" }),
      ]);
      if (messagesRes.ok) { const d = await messagesRes.json(); setUnreadCount(d.count || 0); }
      if (supportRes.ok)  { const d = await supportRes.json(); setSupportUnreadCount(d.count || 0); }
    } catch {}
  }, []);

  const fetchCrmUnread = useCallback(async () => {
    if (!user?.is_admin) return;
    try {
      const r = await fetch(`${API}/admin/tickets/stats`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setCrmUnreadCount(d.total_unread || 0); }
    } catch {}
  }, [user?.is_admin]);

  useEffect(() => {
    if (!user) return;
    fetchCart(); fetchUnread(); fetchCrmUnread();
    let destroyed = false;
    let retryDelay = 2000;
    const connect = () => {
      if (destroyed) return;
      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => {
          retryDelay = 2000;
          clearInterval(wsPingRef.current);
          wsPingRef.current = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 30000);
        };
        ws.onmessage = (e) => {
          try {
            if (e.data === "pong") return;
            const msg = JSON.parse(e.data);
            if (msg.type === "new_message" || msg.type === "support_reply") fetchUnread();
            if (msg.type === "ifollowers:live") window.dispatchEvent(new CustomEvent("ifollowers:live"));
            window.dispatchEvent(new CustomEvent("ifollowers_ws", { detail: msg }));
          } catch {}
        };
        ws.onclose = () => {
          clearInterval(wsPingRef.current);
          if (destroyed) return;
          wsRetryRef.current = setTimeout(() => { retryDelay = Math.min(retryDelay * 1.5, 30000); connect(); }, retryDelay);
        };
        ws.onerror = () => ws.close();
      } catch {}
    };
    connect();
    const interval = setInterval(fetchUnread, 300000);
    const onMessagesRead = () => setUnreadCount(0);
    window.addEventListener("ifollowers:messages-read", onMessagesRead);
    return () => {
      destroyed = true;
      clearTimeout(wsRetryRef.current); clearInterval(wsPingRef.current); clearInterval(interval);
      window.removeEventListener("ifollowers:messages-read", onMessagesRead);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [user, fetchCart, fetchUnread]);

  const isActive = (path) => location.pathname === path;
  const totalUnread = supportUnreadCount + unreadCount;
  const supportBadgeLabel = supportUnreadCount > 99 ? "99+" : supportUnreadCount;

  const centerLinks = [
    { path: "/", label: t("nav.home", lang), icon: <Home className="w-4 h-4" /> },
    { path: "/shop", label: lang === "he" ? "המוצרים שלנו" : lang === "ru" ? "Наши продукты" : "Our Products", icon: <Store className="w-4 h-4" /> },
  ];

  const mobileLinks = [
    { path: "/", label: t("nav.home", lang), icon: <Home className="w-4 h-4" /> },
    { path: "/shop", label: lang === "he" ? "המוצרים שלנו" : lang === "ru" ? "Наши продукты" : "Our Products", icon: <Store className="w-4 h-4" /> },
    ...(user ? [
      { path: "/dashboard", label: t("nav.dashboard", lang), icon: <LayoutDashboard className="w-4 h-4" /> },
      { path: "/topup", label: t("nav.topup", lang), icon: <Wallet className="w-4 h-4" /> },
      { path: "/messages", label: t("nav.messages", lang), icon: <Bell className="w-4 h-4" />, badge: unreadCount },
    ] : []),
  ];

  // Bottom tab bar tabs for mobile
  const mobileTabs = user ? [
    { path: "/",          icon: Home,       label: lang === "he" ? "בית"    : lang === "ru" ? "Главная"  : "Home" },
    { path: "/shop",      icon: Store,      label: lang === "he" ? "חנות"   : lang === "ru" ? "Магазин"  : "Shop" },
    { path: "/dashboard", icon: null,       label: lang === "he" ? "אזור אישי" : lang === "ru" ? "Кабинет"  : "Account", center: true },
    { path: "/messages",  icon: Bell,       label: lang === "he" ? "הודעות" : lang === "ru" ? "Сообщения": "Messages", badge: unreadCount },
    ...(user.is_admin
      ? [{ path: "/admin",  icon: Shield,   label: lang === "he" ? "ניהול"  : lang === "ru" ? "Админ"    : "Admin",   accent: "#8B5CF6", href: "https://admin.ifollowers.site" }]
      : [{ path: "#support", icon: Headphones, label: lang === "he" ? "תמיכה" : lang === "ru" ? "Поддержка" : "Support", accent: "#405DE6", badge: supportUnreadCount, action: "support" }]
    ),
  ] : [
    { path: "/",        icon: Home,       label: lang === "he" ? "בית"    : lang === "ru" ? "Главная"  : "Home" },
    { path: "/shop",    icon: Store,      label: lang === "he" ? "חנות"   : lang === "ru" ? "Магазин"  : "Shop" },
    { path: "/login",   icon: null,       label: lang === "he" ? "כניסה"  : lang === "ru" ? "Войти"    : "Login", center: true },
    { path: "/terms",   icon: HelpCircle, label: lang === "he" ? "תנאים"  : lang === "ru" ? "Условия"  : "Terms" },
    { path: "/faq",     icon: MessageCircle, label: lang === "he" ? "שאלות" : lang === "ru" ? "FAQ"    : "FAQ" },
  ];

  return (
    <>
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-200"
      style={{
        background: "var(--bg-primary)",
        borderBottom: scrolled ? "1px solid var(--border-color)" : "1px solid transparent",
        paddingTop: "env(safe-area-inset-top)",
      }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-14">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <span className="text-lg font-black font-outfit" style={{ color: "#8B5CF6" }}>iFollowers</span>
          </Link>

          {/* Center nav — абсолютно по центру */}
          <div className="hidden md:flex items-center gap-6 absolute left-1/2 -translate-x-1/2">
            {centerLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                className="text-sm font-medium transition-colors duration-150 whitespace-nowrap"
                style={{ color: isActive(link.path) ? "var(--text-primary)" : "var(--text-secondary)" }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right */}
          <div className="flex items-center gap-1.5">

            {/* Balance */}
            {user && (
              <Link
                to="/topup"
                data-testid="nav-balance-chip"
                className="hidden md:flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-bold"
                style={{ background: "rgba(247,119,55,0.12)", color: "#F77737" }}
              >
                <Wallet className="w-3.5 h-3.5" />
                {(user.ipoints || 0).toLocaleString()} iP
              </Link>
            )}

            {/* Language picker — mobile only */}
            <div className="md:hidden">
              <LangDropdown />
            </div>

            {/* Accessibility — mobile only (floating btn covers desktop) */}
            <button
              className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5"
              style={{ color: "#1565C0" }}
              onClick={() => window.dispatchEvent(new CustomEvent("open-a11y"))}
              aria-label="Accessibility"
            >
              <Accessibility className="w-4 h-4" strokeWidth={2.2} />
            </button>

            {/* Support — mobile only, logged-in only */}
            {user && (
              <button
                className="md:hidden relative w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5"
                style={{ color: "var(--text-primary)" }}
                onClick={() => window.dispatchEvent(new CustomEvent("open-support"))}
                aria-label={lang === "he" ? "תמיכה" : lang === "ru" ? "Поддержка" : "Support"}
              >
                <Headphones className="w-4 h-4" />
                {supportUnreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full flex items-center justify-center text-[9px] font-black text-white"
                    style={{ background: "#E1306C" }}>
                    {supportBadgeLabel}
                  </span>
                )}
              </button>
            )}

            {/* Settings — mobile only */}
            <Link
              to="/dashboard?tab=settings"
              className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5"
              style={{ color: "var(--text-primary)" }}
              aria-label={lang === "he" ? "הגדרות" : lang === "ru" ? "Настройки" : "Settings"}
            >
              <Settings className="w-4 h-4" />
            </Link>

            {/* User dropdown */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative hidden md:flex items-center gap-2 h-8 px-2.5 rounded-lg transition-colors hover:bg-white/5 overflow-visible"
                    data-testid="user-menu-trigger"
                    aria-label={lang === "he" ? "תפריט משתמש" : lang === "ru" ? "Меню пользователя" : "User menu"}
                    aria-haspopup="true"
                  >
                    {user.picture
                      ? <img src={user.picture} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
                      : <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white flex-shrink-0"
                          style={{ background: "linear-gradient(135deg,#8B5CF6,#E1306C)" }}>
                          {(user.name || user.email || "?")[0].toUpperCase()}
                        </div>
                    }
                    <span className="text-xs font-medium max-w-[72px] truncate" style={{ color: "var(--text-secondary)" }}>
                      {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                    </span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                    {totalUnread > 0 && (
                      <span className={`absolute -top-1 min-w-[15px] h-[15px] px-0.5 rounded-full flex items-center justify-center text-[9px] font-black text-white ${isRTL ? "-left-1" : "-right-1"}`}
                        style={{ background: "#E1306C" }}>
                        {totalUnread > 99 ? "99+" : totalUnread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align={isRTL ? "start" : "end"}
                  sideOffset={8}
                  style={{
                    width: "252px",
                    background: "var(--bg-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "16px",
                    padding: "0",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                  }}
                >
                  {/* User row */}
                  <div className="px-3 pt-3 pb-3">
                    <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl" style={{ background: "var(--surface-1)" }}>
                      {user.picture
                        ? <img src={user.picture} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                        : <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-white text-sm flex-shrink-0"
                            style={{ background: "linear-gradient(135deg,#8B5CF6,#E1306C)" }}>
                            {(user.name || "?")[0].toUpperCase()}
                          </div>
                      }
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{user.name || user.email}</div>
                        {user.name && <div className="text-[11px] font-medium mt-0.5 truncate" style={{ color: "var(--text-secondary)" }}>{user.email}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Balance row */}
                  <div className="px-3 pb-3">
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl" style={{ background: "var(--surface-1)" }}>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: "var(--text-secondary)" }}>
                          {lang === "he" ? "יתרה" : lang === "ru" ? "Баланс" : "Balance"}
                        </div>
                        <div className="text-base font-black font-outfit" style={{ color: "#F77737" }}>
                          {(user.ipoints || 0).toLocaleString()} iP
                        </div>
                      </div>
                      <Link
                        to="/topup"
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:opacity-90"
                        style={{ background: "linear-gradient(135deg,#F77737,#E1306C)", color: "#fff" }}
                      >
                        {lang === "he" ? "טען" : lang === "ru" ? "Пополнить" : "Top Up"}
                      </Link>
                    </div>
                  </div>

                  <div className="mx-3" style={{ height: "1px", background: "var(--border-color)" }} />

                  {/* Nav items */}
                  <div className="p-2">
                    <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                      <Link to="/dashboard" className="flex items-center gap-2.5">
                        <LayoutDashboard className="w-4 h-4" />
                        {lang === "he" ? "אזור אישי" : lang === "ru" ? "Личный кабинет" : "Dashboard"}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                      <Link to="/dashboard?tab=settings" className="flex items-center gap-2.5">
                        <Settings className="w-4 h-4" />
                        {lang === "he" ? "הגדרות" : lang === "ru" ? "Настройки" : "Settings"}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                      <Link to="/messages" className="flex items-center gap-2.5">
                        <MessageCircle className="w-4 h-4" />
                        <span className="flex-1">{lang === "he" ? "הודעות" : lang === "ru" ? "Сообщения" : "Messages"}</span>
                        {unreadCount > 0 && <span className="bg-[#8B5CF6] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{unreadCount}</span>}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium"
                      style={{ color: "var(--text-secondary)" }}
                      onClick={() => window.dispatchEvent(new CustomEvent("open-support"))}
                    >
                      <div className="flex items-center gap-2.5 w-full">
                        <Headphones className="w-4 h-4" />
                        <span className="flex-1">{lang === "he" ? "שירות לקוחות" : lang === "ru" ? "Поддержка" : "Support"}</span>
                        {supportUnreadCount > 0 && <span className="bg-[#E1306C] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{supportUnreadCount}</span>}
                      </div>
                    </DropdownMenuItem>
                  </div>

                  {user.is_admin && (
                    <>
                      <div className="mx-3" style={{ height: "1px", background: "var(--border-color)" }} />
                      <div className="p-2">
                        <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                          <a href="https://admin.ifollowers.site" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5"><Shield className="w-4 h-4" />{t("nav.admin", lang)}</a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                          <a href="https://crm.ifollowers.site" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5">
                            <MessageSquare className="w-4 h-4" />
                            <span className="flex-1">CRM</span>
                            {crmUnreadCount > 0 && <span className="bg-[#E1306C] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{crmUnreadCount > 99 ? "99+" : crmUnreadCount}</span>}
                          </a>
                        </DropdownMenuItem>
                      </div>
                    </>
                  )}

                  <div className="mx-3" style={{ height: "1px", background: "var(--border-color)" }} />
                  <div className="p-2">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium"
                        style={{ color: "var(--text-secondary)" }}>
                        <img src={LANG_OPTIONS.find(o => o.code === lang)?.flag} alt={lang} style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2, flexShrink: 0 }} />
                        <span style={{ flex: 1, marginLeft: 8 }}>{LANG_OPTIONS.find(o => o.code === lang)?.name}</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent style={{ minWidth: 160 }}>
                        {LANG_OPTIONS.map(({ code, flag, name }) => (
                          <DropdownMenuItem
                            key={code}
                            onClick={() => handleLangSelect(code)}
                            className={`cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium${justSelectedLang === code ? " animate-lang-pop" : ""}`}
                            style={{
                              color: lang === code ? "#8B5CF6" : "var(--text-secondary)",
                              background: lang === code && justSelectedLang !== code ? "rgba(139,92,246,0.08)" : "transparent",
                            }}>
                            <div className="flex items-center gap-2.5 w-full">
                              <img
                                src={flag}
                                alt={name}
                                className={justSelectedLang === code ? "animate-lang-flag" : ""}
                                style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
                              />
                              <span className="flex-1">{name}</span>
                              {lang === code && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "#8B5CF6" }} />}
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </div>

                  <div className="mx-3" style={{ height: "1px", background: "var(--border-color)" }} />
                  <div className="p-2 pb-2.5">
                    <DropdownMenuItem onClick={logout} className="cursor-pointer rounded-xl px-3 py-2 text-sm font-medium text-red-400" data-testid="logout-btn">
                      <div className="flex items-center gap-2.5"><LogOut className="w-4 h-4" />{t("nav.logout", lang)}</div>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={login}
                className="hidden md:inline-flex text-white rounded-lg px-4 text-xs font-bold h-8"
                style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}
                data-testid="login-btn"
              >
                {t("nav.login", lang)}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Legacy mobile menu — kept for tour compatibility, hidden visually on mobile */}
      {mobileOpen && (
        <div className="hidden" data-testid="mobile-menu" aria-hidden="true" />
      )}
    </nav>

    {/* ── Mobile: round FAB just below navbar, top-right ── */}
    {itemCount > 0 && !sidebarOpen && (
      <button
        key={`mob-fab-${itemCount}`}
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed z-40 animate-cart-tab-in flex items-center justify-center"
        style={{
          top: "calc(env(safe-area-inset-top) + 64px)",
          [isRTL ? "left" : "right"]: 16,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #E1306C 0%, #833AB4 100%)",
          color: "#fff",
          boxShadow: "0 6px 24px rgba(225,48,108,0.55), 0 2px 8px rgba(0,0,0,0.25)",
        }}
        data-testid="mobile-cart-fab"
        aria-label="Open cart"
      >
        <ShoppingCart style={{ width: 22, height: 22 }} />
        {itemCount > 0 && (
          <span
            className="absolute flex items-center justify-center font-black text-white"
            style={{
              top: 1, right: 1,
              minWidth: 18, height: 18,
              borderRadius: "50%",
              background: "#fff",
              color: "#E1306C",
              fontSize: 9,
              border: "1.5px solid #E1306C",
            }}
          >
            {itemCount > 9 ? "9+" : itemCount}
          </span>
        )}
      </button>
    )}

    {/* ── Desktop: right-edge cart tab, vertically centered ──
        Wrapper handles Y-centering; inner button animates independently (no transform conflict) */}
    {itemCount > 0 && !sidebarOpen && (
      <div
        key={`desktop-cart-${itemCount}`}
        className="hidden md:block fixed z-40 pointer-events-none"
        style={{
          [isRTL ? "left" : "right"]: 0,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className={`pointer-events-auto flex flex-col items-center justify-center gap-1.5 ${isRTL ? "animate-cart-tab-in-rtl" : "animate-cart-tab-in"}`}
          style={{
            padding: "16px 11px",
            borderRadius: isRTL ? "0 16px 16px 0" : "16px 0 0 16px",
            background: "linear-gradient(160deg, #E1306C 0%, #833AB4 100%)",
            color: "#fff",
            boxShadow: isRTL
              ? "6px 0 28px rgba(131,58,180,0.5), 2px 0 10px rgba(225,48,108,0.3)"
              : "-6px 0 28px rgba(131,58,180,0.5), -2px 0 10px rgba(225,48,108,0.3)",
            minWidth: 46,
            transition: "padding 0.18s ease, box-shadow 0.18s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.padding = "16px 16px"; }}
          onMouseLeave={e => { e.currentTarget.style.padding = "16px 11px"; }}
          data-testid="cart-edge-tab"
          aria-label="Open cart"
        >
          <ShoppingCart style={{ width: 18, height: 18 }} />
          <span className="text-xs font-black font-outfit leading-none">{itemCount}</span>
        </button>
      </div>
    )}

    {/* ── Mobile Bottom Tab Bar (floating glass pill) ── */}
    <div
      className="md:hidden fixed left-3 right-3 z-50 transition-all duration-200"
      style={{
        bottom: "calc(env(safe-area-inset-bottom) + 12px)",
        background: "var(--bg-secondary)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
        border: "1px solid var(--border-color)",
        borderRadius: "22px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2)",
        opacity: supportWidgetOpen ? 0 : 1,
        transform: supportWidgetOpen ? "translateY(20px)" : "translateY(0)",
        pointerEvents: supportWidgetOpen ? "none" : undefined,
      }}
      data-testid="mobile-tab-bar"
    >
      <div className="flex items-center" style={{ height: 62, padding: "0 4px" }}>
        {mobileTabs.map((tab) => {
          const TabIcon = tab.icon;
          const active = tab.action ? false : isActive(tab.path);
          const color = active ? (tab.accent || "#8B5CF6") : "var(--text-secondary)";
          const tabProps = {
            key: tab.path,
            "data-testid": `tab-${tab.path.replace("/", "") || "home"}`,
            className: "relative flex flex-col items-center justify-center flex-1 gap-1",
            style: { color, minHeight: 54, transition: "color 160ms cubic-bezier(0.23,1,0.32,1)" },
          };
          const TabWrapper = tab.action
            ? ({ children, ...p }) => <button {...p} onClick={() => window.dispatchEvent(new CustomEvent("open-support"))}>{children}</button>
            : tab.href
            ? ({ children, ...p }) => <a {...p} href={tab.href} target="_blank" rel="noopener noreferrer">{children}</a>
            : ({ children, ...p }) => <Link {...p} to={tab.path}>{children}</Link>;
          return (
            <TabWrapper {...tabProps}>
              {/* Center tab — icon + label shifted up together */}
              {tab.center ? (
                <div className="flex flex-col items-center gap-1" style={{ transform: "translateY(-5px)" }}>
                  <div className="relative"
                    style={{ transition: "transform 160ms cubic-bezier(0.23,1,0.32,1)", transform: active ? "scale(1.07)" : "scale(1)" }}>
                    {user
                      ? user.picture
                          ? <img src={user.picture} alt=""
                              className="w-9 h-9 rounded-full object-cover"
                              style={{
                                border: active ? "2px solid #8B5CF6" : "2px solid var(--border-color)",
                                boxShadow: active ? "0 0 0 3px rgba(139,92,246,0.18)" : "none",
                                transition: "border-color 160ms cubic-bezier(0.23,1,0.32,1), box-shadow 160ms cubic-bezier(0.23,1,0.32,1)",
                              }} />
                          : <div className="w-9 h-9 rounded-full flex items-center justify-center"
                              style={{
                                background: active ? "linear-gradient(135deg,#8B5CF6,#4F46E5)" : "var(--surface-3)",
                                boxShadow: active ? "0 0 0 3px rgba(139,92,246,0.18)" : "none",
                                transition: "background 160ms cubic-bezier(0.23,1,0.32,1), box-shadow 160ms cubic-bezier(0.23,1,0.32,1)",
                              }}>
                              <UserCircle className="w-5 h-5" style={{ color: active ? "#fff" : "var(--text-secondary)" }} />
                            </div>
                      : <div className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{
                              background: active ? "linear-gradient(135deg,#8B5CF6,#4F46E5)" : "var(--surface-3)",
                              boxShadow: active ? "0 0 0 3px rgba(139,92,246,0.18)" : "none",
                              transition: "background 160ms cubic-bezier(0.23,1,0.32,1), box-shadow 160ms cubic-bezier(0.23,1,0.32,1)",
                            }}>
                            <UserCircle className="w-5 h-5" style={{ color: active ? "#fff" : "var(--text-secondary)" }} />
                          </div>
                    }
                    {totalUnread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full flex items-center justify-center text-[9px] font-black text-white"
                        style={{ background: "#E1306C", border: "1.5px solid var(--bg-primary)" }}>
                        {totalUnread > 9 ? "9+" : totalUnread}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] font-semibold leading-none tracking-tight" style={{ color }}>
                    {tab.label}
                  </span>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <TabIcon className="w-5 h-5" />
                    {tab.badge > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full flex items-center justify-center text-[9px] font-black text-white"
                        style={{ background: "#E1306C", border: "1.5px solid var(--bg-primary)" }}>
                        {tab.badge > 9 ? "9+" : tab.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] font-semibold leading-none tracking-tight" style={{ color }}>
                    {tab.label}
                  </span>
                  {active && (
                    <span className="absolute bottom-1 w-1 h-1 rounded-full"
                      style={{ background: tab.accent || "#8B5CF6" }} />
                  )}
                </>
              )}
            </TabWrapper>
          );
        })}
      </div>
    </div>
    </>
  );
}
